import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Inbox, Package, School as SchoolIcon, Store, ShoppingBag,
  Wallet, Save, Plus, X, Mail, Phone, Building2, MapPin, FileText,
  Bell, RefreshCw, CheckCircle2, AlertTriangle, Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/super/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/super/schools", label: "Schools", icon: SchoolIcon },
  { to: "/super/products", label: "Products", icon: Package },
  { to: "/super/vendors", label: "Vendors", icon: Store },
  { to: "/super/orders", label: "Orders & Ops", icon: ShoppingBag },
  { to: "/super/finance", label: "Finance", icon: Wallet },
  { to: "/super/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/super/settings", label: "Settings", icon: SettingsIcon },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INPUT_CLS = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const BTN_PRIMARY = "inline-flex items-center gap-2 rounded-md bg-blue-700 text-white px-4 py-2 text-sm font-semibold hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed transition";

const emptyForm = {
  business_profile: {
    company_name: "",
    legal_name: "",
    gstin: "",
    address: "",
    support_email: "",
    support_phone: "",
  },
  notifications: {
    email_enabled: true,
    notification_recipients: [],
  },
};

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [newRecipient, setNewRecipient] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/settings");
      setForm({
        business_profile: { ...emptyForm.business_profile, ...(data.business_profile || {}) },
        notifications: {
          email_enabled: data?.notifications?.email_enabled ?? true,
          notification_recipients: data?.notifications?.notification_recipients || [],
        },
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    setNotifLoading(true);
    try {
      const { data } = await api.get("/admin/notifications?limit=15");
      setNotifications(Array.isArray(data) ? data : []);
    } catch (e) {
      // Silent — user may not have permission if role changes mid-session.
    } finally {
      setNotifLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadNotifications();
  }, []);

  const setBP = (key, val) => setForm((f) => ({ ...f, business_profile: { ...f.business_profile, [key]: val } }));
  const setNotif = (key, val) => setForm((f) => ({ ...f, notifications: { ...f.notifications, [key]: val } }));

  const addRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (!email) {
      return;
    }
    if (!EMAIL_RE.test(email)) {
      toast.error("Enter a valid email address (e.g. ops@yourdomain.com)");
      return;
    }
    if (form.notifications.notification_recipients.includes(email)) {
      toast.error("That email is already in the list");
      return;
    }
    setNotif("notification_recipients", [...form.notifications.notification_recipients, email]);
    setNewRecipient("");
  };

  const removeRecipient = (email) => {
    setNotif("notification_recipients",
      form.notifications.notification_recipients.filter((e) => e !== email));
  };

  const save = async () => {
    // Client-side hygiene.
    const bp = { ...form.business_profile };
    if (bp.support_email && !EMAIL_RE.test(bp.support_email)) {
      toast.error("Support email is not a valid email address"); return;
    }
    setSaving(true);
    try {
      const payload = {
        business_profile: bp,
        notifications: {
          email_enabled: !!form.notifications.email_enabled,
          notification_recipients: form.notifications.notification_recipients,
        },
      };
      const { data } = await api.put("/admin/settings", payload);
      setForm({
        business_profile: { ...emptyForm.business_profile, ...(data.business_profile || {}) },
        notifications: {
          email_enabled: data?.notifications?.email_enabled ?? true,
          notification_recipients: data?.notifications?.notification_recipients || [],
        },
      });
      toast.success("Settings saved");
      loadNotifications();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout nav={NAV} title="Platform Settings">
      <div className="max-w-4xl space-y-6" data-testid="super-settings-page">
        {/* Business profile */}
        <section className="sl-card p-6" data-testid="settings-business-card">
          <header className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
              <Building2 size={20} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg text-slate-900">Business Profile</h2>
              <p className="text-sm text-slate-500">Legal + contact details shown in invoices and outbound emails.</p>
            </div>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Company / display name" testid="f-company-name">
              <input className={INPUT_CLS} value={form.business_profile.company_name}
                onChange={(e) => setBP("company_name", e.target.value)}
                data-testid="input-company-name" placeholder="SchoolLane" />
            </Field>
            <Field label="Legal name" testid="f-legal-name">
              <input className={INPUT_CLS} value={form.business_profile.legal_name}
                onChange={(e) => setBP("legal_name", e.target.value)}
                data-testid="input-legal-name"
                placeholder="SchoolLane Technologies Pvt Ltd" />
            </Field>
            <Field label="GSTIN" testid="f-gstin">
              <div className="relative">
                <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={INPUT_CLS + " pl-9 font-mono uppercase"} value={form.business_profile.gstin}
                  onChange={(e) => setBP("gstin", e.target.value.toUpperCase())}
                  data-testid="input-gstin"
                  placeholder="29ABCDE1234F1Z5" maxLength={32} />
              </div>
            </Field>
            <Field label="Support phone" testid="f-support-phone">
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={INPUT_CLS + " pl-9"} value={form.business_profile.support_phone}
                  onChange={(e) => setBP("support_phone", e.target.value)}
                  data-testid="input-support-phone"
                  placeholder="+91 9674259387" />
              </div>
            </Field>
            <Field label="Support email" testid="f-support-email">
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={INPUT_CLS + " pl-9"} type="email"
                  value={form.business_profile.support_email}
                  onChange={(e) => setBP("support_email", e.target.value)}
                  data-testid="input-support-email"
                  placeholder="schoollane.in@gmail.com" />
              </div>
            </Field>
            <Field label="Address" testid="f-address" full>
              <div className="relative">
                <MapPin size={16} className="absolute left-3 top-3 text-slate-400" />
                <textarea className={INPUT_CLS + " pl-9 min-h-[80px]"}
                  value={form.business_profile.address}
                  onChange={(e) => setBP("address", e.target.value)}
                  data-testid="input-address"
                  placeholder="Bengaluru, Karnataka, India" />
              </div>
            </Field>
          </div>
        </section>

        {/* Notifications */}
        <section className="sl-card p-6" data-testid="settings-notifications-card">
          <header className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center">
              <Bell size={20} />
            </div>
            <div className="flex-1">
              <h2 className="font-heading font-semibold text-lg text-slate-900">Order Notifications</h2>
              <p className="text-sm text-slate-500">
                Emails sent via Resend the moment a school places a new order.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox"
                checked={!!form.notifications.email_enabled}
                onChange={(e) => setNotif("email_enabled", e.target.checked)}
                data-testid="toggle-email-enabled"
                className="w-4 h-4 accent-emerald-600" />
              <span>Email enabled</span>
            </label>
          </header>

          <div className="mb-4">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Notification recipients
            </div>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="recipients-list">
              {form.notifications.notification_recipients.length === 0 && (
                <span className="text-sm text-slate-500 italic">
                  No recipients yet — add at least one to receive order alerts.
                </span>
              )}
              {form.notifications.notification_recipients.map((e) => (
                <span key={e}
                  data-testid={`chip-recipient-${e}`}
                  className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full pl-3 pr-1 py-1 text-sm">
                  <Mail size={12} />
                  {e}
                  <button onClick={() => removeRecipient(e)}
                    data-testid={`remove-recipient-${e}`}
                    className="ml-1 w-5 h-5 rounded-full hover:bg-slate-200 flex items-center justify-center"
                    aria-label={`Remove ${e}`}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input className={INPUT_CLS + " flex-1"} type="email"
                value={newRecipient}
                onChange={(e) => setNewRecipient(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
                data-testid="input-new-recipient"
                placeholder="ops@schoollane.in" />
              <button onClick={addRecipient} data-testid="btn-add-recipient"
                className={BTN_PRIMARY + " whitespace-nowrap"}>
                <Plus size={16} /> Add
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Every recipient gets a copy of the order email (school name, order number, total, item list, and a deep link to the Ops Console).
            </p>
          </div>

          <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
            <strong className="text-slate-700">Provider:</strong> Resend.
            {" "}Set <code className="text-[11px] bg-slate-50 border border-slate-200 rounded px-1">RESEND_API_KEY</code>
            {" "}in the backend environment to enable delivery. Until then, emails are queued and audited but not sent.
            {" "}<span className="text-slate-400">WhatsApp: coming soon.</span>
          </div>
        </section>

        {/* Sticky save bar */}
        <div className="sticky bottom-4 z-10 flex justify-end">
          <button onClick={save} disabled={saving || loading}
            data-testid="btn-save-settings"
            className={BTN_PRIMARY + " shadow-lg"}>
            {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>

        {/* Recent notifications journal */}
        <section className="sl-card p-6" data-testid="settings-notifications-log">
          <header className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading font-semibold text-lg text-slate-900">Recent notifications</h2>
              <p className="text-sm text-slate-500">Last 15 email dispatch attempts.</p>
            </div>
            <button onClick={loadNotifications} disabled={notifLoading}
              data-testid="btn-refresh-notifications"
              className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-sm">
              <RefreshCw size={14} className={notifLoading ? "animate-spin" : ""} /> Refresh
            </button>
          </header>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-left px-3 py-2">Recipient</th>
                  <th className="text-left px-3 py-2">Subject</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {notifications.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">
                    No notifications logged yet.
                  </td></tr>
                )}
                {notifications.map((n) => (
                  <tr key={n.id} className="border-t border-slate-100"
                    data-testid={`notif-row-${n.id}`}>
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {(n.created_at || "").slice(0, 19).replace("T", " ")}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-700">
                        {n.kind}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-700">{n.recipient_email || "—"}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[280px]">{n.subject || "—"}</td>
                    <td className="px-3 py-2">
                      {n.status === "sent" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium">
                          <CheckCircle2 size={14} /> sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium"
                          title={n.failure_reason || ""}>
                          <AlertTriangle size={14} /> {n.status || "failed"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}

function Field({ label, children, testid, full }) {
  return (
    <label className={"block " + (full ? "md:col-span-2" : "")} data-testid={testid}>
      <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
